import { Label } from '@unistocks/components/form/components/label';
import {
  FormFieldInjectedProps,
  withLabelAndValidation
} from '@unistocks/components/form/components/with-label-and-validation';
import { FormComponentPropsBase, FormFieldOmittedProps, FormFieldTransform } from '@unistocks/components/form/model';
import { Column, Row } from '@unistocks/components/layout/flex';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import { isFunction } from '@unistocks/shared/utils/util';
import { PropsWithRef, SelectOption } from '@unistocks/types';
import React, { memo, useCallback } from 'react';
import { FieldValues } from 'react-hook-form';
import {
  isWeb,
  ListItem,
  RadioGroup,
  RadioGroupProps,
  Separator,
  useComposedRefs,
  useTheme
} from 'tamagui';

export type RadioOption<T = any> = SelectOption<T>;

type RadioGroupPropsBase<TValue = any, TFieldValues extends FieldValues = FieldValues> = Omit<
  FormComponentPropsBase<TFieldValues>,
  'valueTransform'
> &
  Omit<RadioGroupProps, FormFieldOmittedProps | 'value' | 'onValueChange'> & {
    options: ReadonlyArray<RadioOption<TValue>>;
    size?: 'sm' | 'lg';
    valueTransform?: FormFieldTransform<TValue, string>;
    onValueChange?: (value: TValue) => void;
  };

export type FormRadioGroupProps<
  TValue = any,
  TFieldValues extends FieldValues = FieldValues
> = PropsWithRef<RadioGroupPropsBase<TValue, TFieldValues>, typeof RadioGroup>;
export type FormRadioGroupRef = FormRadioGroupProps['ref'];

const FormRadioGroupComponent = memo<FormRadioGroupProps & FormFieldInjectedProps>(
  ({
    ref: refProp,
    name,
    id,
    field,
    fieldState,
    isRequired,
    isDisabled,
    options,
    size = 'sm',
    valueTransform,
    onValueChange: onValueChangeIn,
    dependsOn,
    ...props
  }) => {
    const composedRef = useComposedRefs(refProp, field.ref);
    const { unistocks } = useTheme();

    const onValueChange = useCallback(
      (value: string) => {
        let transformedValue = value;
        if (isFunction(valueTransform?.output)) {
          transformedValue = valueTransform.output(value);
        }

        field.onChange(transformedValue);
        onValueChangeIn?.(value);
      },
      [field, onValueChangeIn, valueTransform]
    );

    const renderRadioGroupWeb = () => {
      return (
        <Column gap={'$2'}>
          {options.map(({ key, title, value }) => (
            <Row key={key} gap={'$3'} alignItems={'center'} cursor="pointer">
              <RadioGroup.Item
                id={`${id}_${key}`}
                value={valueTransform?.input?.(value) ?? value}
                size={'$3'}
              >
                <RadioGroup.Indicator />
              </RadioGroup.Item>

              <Label htmlFor={`${id}_${key}`} fontWeight={'$4'} cursor="pointer">
                {title ?? key}
              </Label>
            </Row>
          ))}
        </Column>
      );
    };

    const renderRadioGroupNative = () => {
      return options.map(({ key, title, value: optionValue, icon }, i, arr) => {
        const isSelected =
          (valueTransform?.input?.(optionValue) ?? optionValue) ===
          (valueTransform?.input?.(field.value) ?? field.value);

        return (
          <React.Fragment key={key}>
            <ListItem
              backgroundColor={isSelected ? '$color4' : undefined}
              size={'$5'}
              gap={'$3'}
              px={'$3'}
              cursor="pointer"
              disabled={isDisabled}
              title={title ?? key}
              icon={icon}
              scaleIcon={1.4}
              iconAfter={
                <RadioGroup.Item
                  id={`${id}_${key}`}
                  value={valueTransform?.input?.(optionValue) ?? optionValue}
                >
                  <RadioGroup.Indicator />
                </RadioGroup.Item>
              }
              onPressIn={withFeedback}
              onPress={() => onValueChange(optionValue)}
            />
            {i !== arr.length - 1 ? <Separator /> : null}
          </React.Fragment>
        );
      });
    };

    return (
      <RadioGroup
        ref={composedRef}
        id={id}
        name={name}
        mt={'$1'}
        accentColor={unistocks.val}
        value={valueTransform?.input?.(field.value) ?? field.value}
        onValueChange={onValueChange}
        disabled={isDisabled}
        required={isRequired}
        orientation="vertical"
        {...props}
      >
        {isWeb ? renderRadioGroupWeb() : renderRadioGroupNative()}
      </RadioGroup>
    );
  }
);

FormRadioGroupComponent.displayName = 'FormRadioGroup';

export const FormRadioGroup = withLabelAndValidation<FormRadioGroupRef, FormRadioGroupProps>(
  FormRadioGroupComponent
) as <TValue = any, TFieldValues extends FieldValues = FieldValues>(
  props: FormRadioGroupProps<TValue, TFieldValues>
) => React.ReactElement<FormRadioGroupProps<TValue, TFieldValues>>;
